// Pure subdivision-viability assessor.
//
// Given a parcel + zone + overlay set, answer the two big questions:
//   1. Can it be subdivided?
//   2. How many dwellings?
//
// Distinguishes "detached" subdivision (each new home on its own Torrens
// lot) from "attached / co-located" (row, semi-detached, or under the
// 2025 Co-located Housing overlay) which typically allows smaller site
// area per dwelling.
//
// Outputs a verdict, a maximum dwelling count per housing form, and a
// list of blocking/complicating factors — so the UI can both give a
// headline ("1 → 3 likely") and explain WHY.

import { minSitePerDwelling, minFrontage } from './zones.js';

/**
 * @typedef {Object} SubdivisionInput
 * @property {number|null} area_m2
 * @property {number|null} frontage_m
 * @property {string|null} zone_code    SAPPA zone code e.g. 'GN', 'EN'
 * @property {string|null} zone_name    Full name, used as fallback lookup
 * @property {string|null} zone_category
 * @property {Object|null} overlay_summary   SAPPA TNV summary
 * @property {Array<{name: string, code?: string, value?: string}>} overlays
 * @property {boolean|null} is_corner
 */

/** @typedef {'strong'|'possible'|'marginal'|'unlikely'|'blocked'|'not_residential'} Verdict */

const HOUSING_FORMS = ['detached', 'attached'];

// Overlays that are hard/soft blockers for subdivision. Each has a
// "severity" that flows into the verdict.
const OVERLAY_IMPACT = {
  // Hard — subdivision essentially blocked
  'historic area': { severity: 'hard', label: 'Historic Area — demolition typically refused, substantial design review' },
  'state heritage place': { severity: 'hard', label: 'State Heritage Place' },
  'local heritage place': { severity: 'hard', label: 'Local Heritage Place' },
  'character area': { severity: 'hard', label: 'Character Area — demolition and infill tightly controlled' },
  // Significant complications
  'bushfire': { severity: 'major', label: 'Bushfire hazard — compliance adds cost + scope limits' },
  'flood': { severity: 'major', label: 'Flood risk — engineered habitable-floor levels required' },
  'hazards (flooding)': { severity: 'major', label: 'Flood hazard' },
  'hills face': { severity: 'major', label: 'Hills Face Zone constraints compound' },
  'regulated and significant tree': { severity: 'major', label: 'Regulated / Significant Trees restrict building footprint' },
  'coastal areas': { severity: 'major', label: 'Coastal Areas overlay' },
  // Minor — worth flagging but don\'t move the verdict
  'airport building heights (regulated)': { severity: 'minor', label: 'Airport building heights (regulated)' },
  'building near airfields': { severity: 'minor', label: 'Near airfield — height + use restrictions' },
  'traffic generating development': { severity: 'minor', label: 'Traffic generating development — access assessment likely' },
  'urban tree canopy': { severity: 'minor', label: 'Urban tree canopy retention required' },
  'prescribed wells area': { severity: 'minor', label: 'Prescribed wells area' },
  'stormwater management': { severity: 'minor', label: 'Stormwater management — WSUD required' },
  'co-located housing': { severity: 'enabler', label: 'Co-located Housing — enables attached/shared formats on smaller sites' },
};

// Attached-housing site-per-dwelling default by base zone code. SAPPA's
// Minimum Site Area TNV is detached-specific; attached/row/group
// typically sits ~50–60% of that.
const ATTACHED_FACTOR = 0.55;

/**
 * @param {SubdivisionInput} input
 */
export function assessSubdivision(input) {
  const {
    area_m2, frontage_m,
    zone_code, zone_name, zone_category,
    overlay_summary = {}, overlays = [],
  } = input;

  const notes = [];
  const overlayFindings = classifyOverlays(overlays);
  const hardBlocks = overlayFindings.filter((f) => f.severity === 'hard');
  const majorFlags = overlayFindings.filter((f) => f.severity === 'major');
  const enablers = overlayFindings.filter((f) => f.severity === 'enabler');

  // Non-residential zones → different game entirely.
  const nonResidential = zone_category && [
    'Employment', 'Infrastructure', 'Tourism',
    'Natural Resources and Environment',
  ].includes(zone_category);
  if (nonResidential) {
    return notApplicable(zone_category, overlayFindings);
  }

  if (area_m2 == null || zone_code == null && zone_name == null) {
    return {
      verdict: 'unknown',
      max_dwellings: { detached: null, attached: null },
      checks: [],
      overlays: overlayFindings,
      notes: ['Need land area + zone to assess. Run a SAPPA lookup first.'],
    };
  }

  // Resolve the effective minimum site area per dwelling — SAPPA TNV
  // overlay wins, else our zone table.
  const tnvMinSite = overlay_summary.min_site_per_dwelling ?? null;
  const zoneMinSite = minSitePerDwelling(zone_code || zone_name);
  const effectiveMinSite = tnvMinSite ?? zoneMinSite;
  const minSiteSource = tnvMinSite != null ? 'SAPPA overlay' : zoneMinSite != null ? 'zone table' : null;

  if (!effectiveMinSite) {
    return {
      verdict: 'unknown',
      max_dwellings: { detached: null, attached: null },
      checks: [],
      overlays: overlayFindings,
      notes: [`No minimum-site-area data for zone ${zone_name ?? zone_code}.`],
    };
  }

  // Find the max N (up to 6) for each housing form.
  const perForm = {};
  for (const form of HOUSING_FORMS) {
    const perDwelling = form === 'attached'
      ? Math.round(effectiveMinSite * ATTACHED_FACTOR)
      : effectiveMinSite;
    perForm[form] = maxDwellingsFor({
      area_m2,
      frontage_m,
      per_dwelling: perDwelling,
      overlay_min_frontage: overlay_summary.min_frontage ?? null,
    });
  }

  const detachedMax = perForm.detached.max;
  const attachedMax = perForm.attached.max;
  const enablersPresent = enablers.length > 0;

  // Pick the verdict from worst-case-view.
  let verdict = pickVerdict({
    detachedMax, attachedMax,
    hardBlocks, majorFlags, enablersPresent,
  });

  // Human notes — the why.
  if (hardBlocks.length) {
    notes.push(`Hard blockers: ${hardBlocks.map((f) => f.label).join(' · ')}`);
  }
  if (majorFlags.length) {
    notes.push(`Complications: ${majorFlags.map((f) => f.label).join(' · ')}`);
  }
  if (enablers.length) {
    notes.push(`Enablers: ${enablers.map((f) => f.label).join(' · ')}`);
  }
  notes.push(
    `Effective minimum site: ${effectiveMinSite} m² per detached dwelling (${minSiteSource}).`,
  );
  if (overlay_summary.min_frontage) {
    notes.push(`SAPPA per-parcel Minimum Frontage overlay: ${overlay_summary.min_frontage} m.`);
  }

  return {
    verdict,
    max_dwellings: {
      detached: detachedMax,
      attached: attachedMax,
    },
    per_form: perForm,
    effective_min_site_per_dwelling: effectiveMinSite,
    effective_min_site_source: minSiteSource,
    overlays: overlayFindings,
    notes,
  };
}

function maxDwellingsFor({ area_m2, frontage_m, per_dwelling, overlay_min_frontage }) {
  const checks = [];
  let max = 0;
  for (let n = 1; n <= 6; n++) {
    const areaRequired = per_dwelling * n;
    const frontageRequired = overlay_min_frontage ?? minFrontage(n);
    const areaOk = area_m2 >= areaRequired;
    const frontageOk = frontage_m == null ? null : frontage_m >= frontageRequired;
    const passes = areaOk && (frontageOk !== false);
    if (passes) max = n;
    checks.push({
      n,
      area_required: areaRequired,
      area_ok: areaOk,
      frontage_required: frontageRequired,
      frontage_ok: frontageOk,
      passes,
    });
    // No point testing larger counts once area fails.
    if (!areaOk) break;
  }
  return { max, checks };
}

function classifyOverlays(overlays) {
  const out = [];
  for (const o of overlays ?? []) {
    const key = String(o.name ?? '').toLowerCase().trim();
    // Try full match first, then prefix match (Historic Area - Un11 etc.)
    const impact = OVERLAY_IMPACT[key] ??
      Object.entries(OVERLAY_IMPACT).find(([k]) => key.startsWith(k))?.[1];
    if (!impact) continue;
    out.push({
      key, name: o.name, value: o.value ?? null,
      severity: impact.severity, label: impact.label,
    });
  }
  return out;
}

function pickVerdict({ detachedMax, attachedMax, hardBlocks, majorFlags, enablersPresent }) {
  // Hard-block overlays override the math.
  if (hardBlocks.length) {
    if (enablersPresent && attachedMax >= 2) return 'possible'; // Co-located + heritage, narrow path
    return 'unlikely';
  }
  const max = Math.max(detachedMax, attachedMax);
  if (max === 0) return 'blocked';
  if (max === 1) return 'unlikely';
  if (max === 2) return majorFlags.length ? 'possible' : 'marginal';
  if (max === 3) return majorFlags.length ? 'marginal' : 'possible';
  // max >= 4
  return majorFlags.length ? 'possible' : 'strong';
}

function notApplicable(category, overlays) {
  return {
    verdict: 'not_residential',
    max_dwellings: { detached: null, attached: null },
    checks: [],
    overlays,
    notes: [`Zone category "${category}" isn\u2019t residential — dwelling subdivision rules don\u2019t apply.`],
  };
}

/** Human-readable label + colour hint for a verdict. */
export function verdictPresentation(v) {
  switch (v) {
    case 'strong':         return { label: '1 → 4+ likely',    tone: 'positive' };
    case 'possible':       return { label: 'Subdivision possible', tone: 'positive' };
    case 'marginal':       return { label: 'Marginal',        tone: 'warning' };
    case 'unlikely':       return { label: 'Unlikely',        tone: 'warning' };
    case 'blocked':        return { label: 'Blocked',         tone: 'negative' };
    case 'not_residential': return { label: 'Not residential', tone: 'neutral' };
    default:               return { label: 'Unknown',         tone: 'neutral' };
  }
}
